import { Component, Vue, Watch } from "vue-property-decorator";
import { Modal, Select, Button, Tooltip, FormModel, Input, Spin, Icon, Result } from "ant-design-vue";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import {
	ContentService,
	ContentTextEditModel,
	CreateChapterRequestModel,
	EditContentTextRequestModel
} from "@/services/content";
import { VNode } from "vue/types/umd";
import { MessageHelper, NotificationType, ActionItem, fmtMsg } from "commonui";
import TextTagEditor from "@/components/text-tag-editor/text-tag-editor.vue";
import MatIcon from "@/commonui/components/mat-icon/mat-icon.vue";
import { orderBy, sum } from "lodash";
import { v4 } from "uuid";
import { EmptyGuid } from "@/util/constants";
import { disabledDueToAccessLevel, getRouteAccessLevel, getUniqueChangedWords } from "@/util/func";
import { VersionGroupModule } from "@/modules/versiongroup";
import { ManageChapterLocale } from "@/locales/localeid";
import { CurriculumType, LanguageSkill } from "@/util/enum";

@Component({
	components: {
		Modal,
		Select,
		SelectOption: Select.Option,
		Button,
		Tooltip,
		TextTagEditor,
		Input,
		FormItem: FormModel.Item,
		Spin,
		ActionItem,
		Icon,
		MatIcon,
		Result
	}
})
export default class ManageChapters extends Vue {
	chapterSelectorModalVisible = false;
	titleListModule = getModule(TitleListState);
	selectedTitleForLinking = "";
	addingChapter = false;
	enableSave = true; // For text addition
	editedSomethingInText = false;
	textDetails: ContentTextEditModel[] = [
		{
			id: v4(),
			contentId: EmptyGuid,
			textContent: "",
			previousText: "",
			revision: 0,
			sequence: 1
		}
	];
	private editorRef = "ediRef";
	newChapterTitle = "";
	savingChapter = false;
	linking = false; // When user tries to link
	removingOrMoving = false; // sets to true when moveUP, moveDown or remove is clicked
	public stickyFooterWidth = 500;
	public stickyDefault = { height: 50, bottom: 70 };
	public stickyFooterHeight = this.stickyDefault.height;
	public stickyFooterBottomPos = this.stickyDefault.bottom;
	public stickyFooterRight = 10;

	get possibleChapters() {
		return this.titleListModule.possibleChapters;
	}

	get chapters() {
		return this.titleListModule.editedContentDetails.chapters
			? this.titleListModule.editedContentDetails.chapters
			: [];
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	mounted() {
		this.addChildWidthChangeEventListener();
	}

	@Watch("$route.path")
	addStickyListener() {
		Vue.nextTick(() => this.addChildWidthChangeEventListener());
	}

	addChildWidthChangeEventListener() {
		const text: HTMLDivElement = this.$refs.chapAdd as any;
		const platform = navigator && navigator.platform ? navigator.platform : "";
		const isWindow = /^win/g.test(platform.toLowerCase());
		if (text) {
			const setWidth = () => {
				this.stickyFooterWidth = text.clientWidth + 40;
				// check if scroll is available on windows.
				if (isWindow) {
					const el: HTMLCollectionOf<Element> = document.getElementsByClassName("vc-layout__content");
					if (el && el.length) {
						const children = Array.from(el[0].children);
						const childHeight = sum(children.map((m) => m.clientHeight)) + 9;
						if (childHeight > el[0].clientHeight) {
							this.stickyFooterRight = 27;
						} else {
							this.stickyFooterRight = 10;
						}
					}
				}
			};
			setWidth();
			const win = window as any;
			new win.ResizeObserver(setWidth).observe(text);
			if (isWindow) {
				new win.ResizeObserver(setWidth).observe(document.getElementsByClassName("vc-layout__content")[0]);
			}
		}
	}

	openTextEditor() {
		this.addingChapter = true;
		this.newChapterTitle = "";
		this.enableSave = true;
	}

	closeTextEditor() {
		this.addingChapter = false;
		this.enableSave = false;
	}

	textValidated(isValid: boolean) {
		this.enableSave = isValid;
	}

	onEditedSomething() {
		this.editedSomethingInText = true;
	}

	closeChapterLinkModal() {
		this.chapterSelectorModalVisible = false;
		this.selectedTitleForLinking = "";
	}

	openChapterLinkModal() {
		this.selectedTitleForLinking = this.possibleChapters.length ? this.possibleChapters[0].id : "";
		this.chapterSelectorModalVisible = true;
		if (!this.titleListModule.possibleChapters.length) {
			this.titleListModule.updatePossibleChildComponents(this.titleListModule.editedContentDetails.vcsLevelId);
		}
	}

	linkTitleConfirm() {
		Modal.confirm({
			title: fmtMsg(ManageChapterLocale.ManageChapterLinkTitleConfirmMsg),
			onOk: this.linkTitle
		});
	}

	linkTitle() {
		// While in edit mode
		if (this.titleListModule.editedContentId.length) {
			// add mapping at backend and which would return 'ContentInfoModel'. Add that to chapters of current title
			const sequence = this.titleListModule.getEditedContentDetails.chapters
				? this.titleListModule.getEditedContentDetails.chapters.length + 1
				: 1;
			this.linking = true;
			ContentService.linkChapter(this.titleListModule.editedContentId, this.selectedTitleForLinking, sequence)
				.then((chapterInfo) => {
					this.titleListModule.setContentDetails({
						...this.titleListModule.editedContentDetails,
						chapters: [...this.titleListModule.editedContentDetails.chapters, chapterInfo]
					});
					// remove title from possible chapters
					this.titleListModule.setPossibleChapters(
						this.titleListModule.possibleChapters.filter((f) => f.id !== this.selectedTitleForLinking)
					);
					this.linking = false;
					this.chapterSelectorModalVisible = false;
					Vue.nextTick(() => this.addChildWidthChangeEventListener());
				})
				.catch(() => {
					MessageHelper.Message(NotificationType.Failed, "Unable to link chapter with parent.");
					this.linking = false;
				});
		}
	}

	async unlinkTitle(chapterId: string, title: string) {
		Modal.confirm({
			title: fmtMsg(ManageChapterLocale.MangageChapterUnlinkTitleMsg, { title }),
			onOk: async () => {
				this.removingOrMoving = true;

				// Check if the chapter to link is present in any of exposure related items or teaching activity.
				const parentId = this.titleListModule.editedContentDetails.id;
				const hasReader = this.titleListModule.editedContentDetails.languageSkillId === LanguageSkill.Read;
				const result = await ContentService.isChapterPresentInExposure(chapterId, parentId, hasReader);
				if (result) {
					this.removingOrMoving = false;
					MessageHelper.Message(
						NotificationType.Failed,
						fmtMsg(ManageChapterLocale.MangageChapterUnlinkFailedMsg)
					);
				} else {
					ContentService.unlinkChapter(chapterId)
						.then(() => {
							// remove from content chapters
							let remainingChapters = this.titleListModule.editedContentDetails.chapters.filter(
								(c) => c.id !== chapterId
							);
							remainingChapters = orderBy(remainingChapters, ["sequence"], ["asc"]).map((m, i) => {
								m.sequence = i + 1;
								return m;
							});
							this.titleListModule.setContentDetails({
								...this.titleListModule.editedContentDetails,
								chapters: remainingChapters
							});
							// add to possible
							this.titleListModule.setPossibleChapters([
								...this.titleListModule.possibleChapters,
								{ id: chapterId, title }
							]);

							this.removingOrMoving = false;
							MessageHelper.Message(
								NotificationType.Success,
								fmtMsg(ManageChapterLocale.MangageChapterUnlinkChapterSuccessMsg)
							);
						})
						.catch(() => {
							this.removingOrMoving = false;
							MessageHelper.Message(
								NotificationType.Failed,
								fmtMsg(ManageChapterLocale.MangageChapterUnlinkChapterFailedMsg)
							);
						});
				}
			}
		});
	}

	async saveChapterTitle() {
		const ref: any = this.$refs[this.editorRef];
		const details = ref.updatedTextDetails();

		if (details && Array.isArray(details)) {
			// save the chapter
			const allMismatchedData = getUniqueChangedWords(
				[],
				details.map((m) => m.textContent)
			);
			const wordsToCalculate = allMismatchedData.allMismatched;
			const parent = this.titleListModule.editedContentDetails;

			const model: CreateChapterRequestModel = {
				parentTitleInfo: {
					id: parent.id,
					...this.titleListModule.updatedDetails,
					unit: this.titleListModule.levels.find(
						(l) => l.id === this.titleListModule.updatedDetails.vcsLevelId
					)?.unit
				},
				updateParentInfo: true,
				title: this.newChapterTitle.trim(),
				parentContentId: this.titleListModule.editedContentId,
				addNewWords: true,
				wordsToProcess: wordsToCalculate,
				contentText: details.map((m, index) => {
					return {
						textContent: m.textContent,
						sequence: index + 1,
						code: m.code
					} as EditContentTextRequestModel;
				})
			};

			try {
				this.savingChapter = true;
				const newChapterDetails = await ContentService.createChapter(model);
				const selectedLevel = this.titleListModule.levels.find(
					(l) => l.id === model.parentTitleInfo.vcsLevelId
				);
				const versionId =
					selectedLevel?.curriculumType === CurriculumType.GrapeSEED
						? VersionGroupModule.selectedGsVersion
						: VersionGroupModule.selectedLsVersion;

				ContentService.calculateWords(
					[model.parentContentId, newChapterDetails.id],
					versionId,
					model.parentTitleInfo.vcsLevelId
				);
				this.enableSave = false;
				// update possible chapters if levelId is changed
				if (
					this.titleListModule.editedContentDetails.vcsLevelId !==
					this.titleListModule.updatedDetails.vcsLevelId
				) {
					await this.titleListModule.updatePossibleChildComponents(
						this.titleListModule.updatedDetails.vcsLevelId
					);
				}
				// update editedContentDetails as in updatedDetails
				this.titleListModule.setContentDetails({
					...this.titleListModule.editedContentDetails,
					...this.titleListModule.updatedDetails,
					chapters: [...this.titleListModule.editedContentDetails.chapters, newChapterDetails]
				});
				this.savingChapter = false;
				this.addingChapter = false;
				MessageHelper.Message(
					NotificationType.Success,
					"Chapter '" + this.newChapterTitle.trim() + "' created successfully."
				);
				Vue.nextTick(() => this.addChildWidthChangeEventListener());
			} catch (e) {
				MessageHelper.Message(NotificationType.Failed, "Unable to create a chapter. Please try again.");
				this.savingChapter = false;
			}
		}
	}

	async saveTitleInfo() {
		// saves the information of the title (form info)
		this.titleListModule.setUpdateTitleInfo(true);
	}

	async moveUp(chapterId: string, currentSequence: number) {
		try {
			this.removingOrMoving = true;
			const updatedSequence = await ContentService.updateSequence(
				this.titleListModule.editedContentId,
				chapterId,
				currentSequence - 1
			);
			this.updateSequence(updatedSequence);
			this.removingOrMoving = false;
		} catch (e) {
			this.removingOrMoving = false;
			MessageHelper.Message(NotificationType.Failed, "Unable to move up. Please try again.");
		}
	}

	async moveDown(chapterId: string, currentSequence: number) {
		try {
			this.removingOrMoving = true;
			const updatedSequence = await ContentService.updateSequence(
				this.titleListModule.editedContentId,
				chapterId,
				currentSequence + 1
			);
			this.updateSequence(updatedSequence);
			this.removingOrMoving = false;
		} catch (e) {
			this.removingOrMoving = false;
			MessageHelper.Message(NotificationType.Failed, "Unable to move up. Please try again.");
		}
	}

	private updateSequence(updatedSequence: any[]) {
		// updates sequence of chapters locally
		var chaps = this.titleListModule.editedContentDetails.chapters.map((c) => {
			c.sequence = updatedSequence[c.id];
			return c;
		});
		this.titleListModule.setContentDetails({
			...this.titleListModule.editedContentDetails,
			chapters: orderBy(chaps, ["sequence"], ["asc"])
		});
		this.$forceUpdate();
	}

	handleSearch(value: string, option: VNode) {
		const opt = this.titleListModule.possibleChapters.find((f) => f.id === option.key);
		if (opt && opt.title.match(new RegExp(value, "i"))) {
			return true;
		}
		return false;
	}

	handleChange(value: string) {
		this.selectedTitleForLinking = value;
	}
}
